import React, { createContext, useContext, useState, useEffect, ReactNode, useCallback } from 'react';
import { platformSettingsAPI } from '../lib/api';

export interface BrandingSettings {
  companyName: string;
  loginTitle: string;
  copyrightOwner: string;
  defaultTheme: number;
  availableThemes: string[];
  backgroundColor: string;
  loginColor: string;
  fontColor: string;
  buttonColor: string;
  companyLogo: string | null;
  companyDarkLogo: string | null;
  website: string;
  supportEmail: string;
  salesEmail: string;
  noReplyEmail: string;
  emailSubject: string;
  emailSignature: string;
}

/**
 * Convert hex color to HSL format for CSS custom properties.
 * Returns HSL values as "H S% L%" string (without hsl() wrapper).
 */
function hexToHSL(hex: string): string {
  // Remove # if present
  hex = hex.replace(/^#/, '');

  // Parse hex to RGB
  const r = parseInt(hex.substring(0, 2), 16) / 255;
  const g = parseInt(hex.substring(2, 4), 16) / 255;
  const b = parseInt(hex.substring(4, 6), 16) / 255;

  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  let h = 0;
  let s = 0;
  const l = (max + min) / 2;

  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);

    switch (max) {
      case r:
        h = ((g - b) / d + (g < b ? 6 : 0)) / 6;
        break;
      case g:
        h = ((b - r) / d + 2) / 6;
        break;
      case b:
        h = ((r - g) / d + 4) / 6;
        break;
    }
  }

  // Convert to degrees and percentages
  const hDeg = Math.round(h * 360);
  const sPercent = Math.round(s * 100);
  const lPercent = Math.round(l * 100);

  return `${hDeg} ${sPercent}% ${lPercent}%`;
}

// Default color values (must match DEFAULT_BRANDING)
const DEFAULT_BUTTON_COLOR = '#4f46e5';
const DEFAULT_LOGIN_COLOR = '#4f46e5';
const DEFAULT_BACKGROUND_COLOR = '#ffffff';
const DEFAULT_FONT_COLOR = '#333333';

// Default CSS values from index.css (HSL format without hsl() wrapper)
const CSS_DEFAULT_PRIMARY = '221.2 83.2% 53.3%';
const CSS_DEFAULT_BACKGROUND = '0 0% 100%';
const CSS_DEFAULT_FOREGROUND = '222.2 84% 4.9%';

/**
 * Apply branding colors to CSS custom properties.
 * This allows branding to affect the entire platform UI.
 * When colors are reset to defaults, restore the original CSS values.
 */
function applyBrandingToCSS(branding: BrandingSettings): void {
  const root = document.documentElement;

  // Apply primary/button color (used for buttons, links, accents)
  if (branding.buttonColor) {
    const isDefault = branding.buttonColor.toLowerCase() === DEFAULT_BUTTON_COLOR.toLowerCase();
    if (isDefault) {
      // Reset to CSS default
      root.style.setProperty('--primary', CSS_DEFAULT_PRIMARY);
      root.style.setProperty('--ring', CSS_DEFAULT_PRIMARY);
    } else {
      const primaryHSL = hexToHSL(branding.buttonColor);
      root.style.setProperty('--primary', primaryHSL);
      root.style.setProperty('--ring', primaryHSL);
    }
  }

  // Apply login color (used for sidebar and login accents)
  if (branding.loginColor) {
    const isDefault = branding.loginColor.toLowerCase() === DEFAULT_LOGIN_COLOR.toLowerCase();
    if (isDefault) {
      root.style.removeProperty('--sidebar-primary');
    } else {
      const loginHSL = hexToHSL(branding.loginColor);
      root.style.setProperty('--sidebar-primary', loginHSL);
    }
  }

  // Apply background color (used for page backgrounds)
  if (branding.backgroundColor) {
    const isDefault = branding.backgroundColor.toLowerCase() === DEFAULT_BACKGROUND_COLOR.toLowerCase();
    if (isDefault) {
      // Reset to CSS default
      root.style.setProperty('--background', CSS_DEFAULT_BACKGROUND);
    } else {
      const bgHSL = hexToHSL(branding.backgroundColor);
      root.style.setProperty('--background', bgHSL);
    }
  }

  // Apply font/foreground color (used for text)
  if (branding.fontColor) {
    const isDefault = branding.fontColor.toLowerCase() === DEFAULT_FONT_COLOR.toLowerCase();
    if (isDefault) {
      // Reset to CSS default
      root.style.setProperty('--foreground', CSS_DEFAULT_FOREGROUND);
    } else {
      const fgHSL = hexToHSL(branding.fontColor);
      root.style.setProperty('--foreground', fgHSL);
    }
  }
}

interface BrandingContextType {
  branding: BrandingSettings;
  updateBranding: (settings: Partial<BrandingSettings>) => void;
  loadBrandingFromBackend: () => Promise<void>;
  loadCompanyBranding: () => Promise<void>;
  resetToDefaults: () => void;
  resetColorsOnly: () => void;
  isLoading: boolean;
}

const BrandingContext = createContext<BrandingContextType | undefined>(undefined);

// Default branding values - these are the hardcoded defaults used when:
// 1. No branding is configured for a company
// 2. Colors are reset to defaults
// 3. As fallback before branding is loaded
const DEFAULT_BRANDING: BrandingSettings = {
  companyName: 'Supplier Portal',
  loginTitle: '',
  copyrightOwner: 'Arden Rede Ltd',
  defaultTheme: 1,
  availableThemes: ['theme1', 'theme2'],
  backgroundColor: '#ffffff',
  loginColor: '#4f46e5', // Indigo-600
  fontColor: '#333333',
  buttonColor: '#4f46e5',
  companyLogo: null,
  companyDarkLogo: null,
  website: '',
  supportEmail: '',
  salesEmail: '',
  noReplyEmail: '',
  emailSubject: 'Password Reset',
  emailSignature: 'The Team',
};

// Export for use in other components (e.g., useAdminSettings for reset to defaults)
export { DEFAULT_BRANDING };

/**
 * Provides platform branding settings (company name, colors, emails).
 * Loads from the public branding endpoint on mount -- no authentication required,
 * so the login page can also display branded content.
 */
export const BrandingProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [branding, setBranding] = useState<BrandingSettings>(DEFAULT_BRANDING);
  const [isLoading, setIsLoading] = useState(true);

  const loadBrandingFromBackend = useCallback(async () => {
    setIsLoading(true);
    try {
      // Use public branding endpoint (no auth required) - for login page
      const response = await platformSettingsAPI.getPublicBranding();
      if (response.data) {
        const settings = response.data;
        const newBranding: BrandingSettings = {
          ...DEFAULT_BRANDING,
          companyName: settings.company_name || DEFAULT_BRANDING.companyName,
          loginTitle: settings.login_title || '',
          backgroundColor: settings.background_color || DEFAULT_BRANDING.backgroundColor,
          loginColor: settings.login_color || DEFAULT_BRANDING.loginColor,
          fontColor: settings.font_color || DEFAULT_BRANDING.fontColor,
          buttonColor: settings.button_color || DEFAULT_BRANDING.buttonColor,
          companyLogo: settings.company_logo || null,
          companyDarkLogo: settings.company_dark_logo || null,
        };
        setBranding(newBranding);
        // Apply CSS immediately after loading
        applyBrandingToCSS(newBranding);
      }
    } catch (error) {
      console.error('Failed to load branding settings:', error);
      // Keep defaults on error
    } finally {
      setIsLoading(false);
    }
  }, []);

  /**
   * Load company-specific branding for the authenticated user.
   * This should be called after login to get the user's company branding.
   */
  const loadCompanyBranding = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await platformSettingsAPI.getCompanyBranding();
      if (response.data) {
        const settings = response.data;
        const newBranding: BrandingSettings = {
          ...DEFAULT_BRANDING,
          // Use company branding if set, otherwise use defaults
          companyName: settings.company_name || DEFAULT_BRANDING.companyName,
          loginTitle: settings.login_title || '',
          backgroundColor: settings.background_color || DEFAULT_BRANDING.backgroundColor,
          loginColor: settings.login_color || DEFAULT_BRANDING.loginColor,
          fontColor: settings.font_color || DEFAULT_BRANDING.fontColor,
          buttonColor: settings.button_color || DEFAULT_BRANDING.buttonColor,
          companyLogo: settings.company_logo || null,
          companyDarkLogo: settings.company_dark_logo || null,
        };
        setBranding(newBranding);
        applyBrandingToCSS(newBranding);
      }
    } catch (error) {
      console.error('Failed to load company branding:', error);
      // Keep current branding on error
    } finally {
      setIsLoading(false);
    }
  }, []);

  /**
   * Reset branding to hardcoded defaults.
   * This applies the default colors immediately to the CSS.
   */
  const resetToDefaults = useCallback(() => {
    setBranding(DEFAULT_BRANDING);
    applyBrandingToCSS(DEFAULT_BRANDING);
  }, []);

  /**
   * Reset only the colors to defaults, preserving company name and other settings.
   * This is used when admin clicks "Reset Colors to Default" button.
   */
  const resetColorsOnly = useCallback(() => {
    setBranding(prev => {
      const updated = {
        ...prev,
        backgroundColor: DEFAULT_BRANDING.backgroundColor,
        loginColor: DEFAULT_BRANDING.loginColor,
        fontColor: DEFAULT_BRANDING.fontColor,
        buttonColor: DEFAULT_BRANDING.buttonColor,
      };
      applyBrandingToCSS(updated);
      return updated;
    });
  }, []);

  // Load public branding on mount ONLY if no user is logged in (for login page).
  // If user is logged in (token exists), BrandingSync will load company branding.
  // This prevents a race condition where public branding overwrites company branding.
  useEffect(() => {
    // Check if user might be logged in by looking for auth token
    const hasToken = localStorage.getItem('helix_auth_token') ||
                     sessionStorage.getItem('helix_auth_token') ||
                     localStorage.getItem('token');
    if (!hasToken) {
      // No token = not logged in, load public branding for login page
      loadBrandingFromBackend();
    }
    // If token exists, BrandingSync will handle loading company branding
  }, [loadBrandingFromBackend]);

  // Apply branding colors to CSS custom properties whenever branding changes
  useEffect(() => {
    if (!isLoading) {
      applyBrandingToCSS(branding);
    }
  }, [branding, isLoading]);

  // Update browser tab title when branding changes
  useEffect(() => {
    if (!isLoading && branding.companyName) {
      document.title = branding.companyName;
    }
  }, [branding.companyName, isLoading]);

  const updateBranding = (settings: Partial<BrandingSettings>) => {
    setBranding(prev => ({ ...prev, ...settings }));
  };

  return (
    <BrandingContext.Provider
      value={{
        branding,
        updateBranding,
        loadBrandingFromBackend,
        loadCompanyBranding,
        resetToDefaults,
        resetColorsOnly,
        isLoading,
      }}
    >
      {children}
    </BrandingContext.Provider>
  );
};

/**
 * Accesses platform branding settings (company name, colors, contact emails).
 * Must be used within a {@link BrandingProvider}.
 */
export const useBranding = (): BrandingContextType => {
  const context = useContext(BrandingContext);
  if (!context) {
    throw new Error('useBranding must be used within a BrandingProvider');
  }
  return context;
};
